const TABS_TAG = 'content-tabs'
const TAB_TAG = 'content-tab'

export function defineContentTabs(): void {
  if (typeof window === 'undefined') return

  if (!window.customElements.get(TAB_TAG)) {
    class ContentTabElement extends HTMLElement {
      static observedAttributes = ['active']

      constructor() {
        super()
        this.attachShadow({ mode: 'open' })
      }

      connectedCallback() {
        const root = this.shadowRoot
        if (!root) return
        if (!root.firstChild) {
          root.innerHTML = `
            <style>
              :host { display: none; }
              :host([active]) { display: block; }
              .panel { padding: 16px; background: var(--rich-tabs-panel-bg, #fff); }
            </style>
            <div class="panel" role="tabpanel"><slot></slot></div>`
        }
        this.syncView()
      }

      attributeChangedCallback() {
        this.syncView()
      }

      private syncView() {
        this.shadowRoot?.querySelector('.panel')?.setAttribute('aria-hidden', String(!this.hasAttribute('active')))
      }
    }

    window.customElements.define(TAB_TAG, ContentTabElement)
  }

  if (!window.customElements.get(TABS_TAG)) {
    class ContentTabsElement extends HTMLElement {
      private observer?: MutationObserver

      constructor() {
        super()
        this.attachShadow({ mode: 'open' })
      }

      connectedCallback() {
        const root = this.shadowRoot
        if (!root) return
        if (!root.firstChild) {
          root.innerHTML = `
            <style>
              :host { display: block; margin: 12px 0; }
              .tabs {
                overflow: hidden; border: 1px solid var(--rich-tabs-border, #cbd5e1);
                border-radius: var(--rich-tabs-radius, 6px); background: var(--rich-tabs-bg, #fff);
              }
              .list { display: flex; overflow-x: auto; border-bottom: 1px solid var(--rich-tabs-border, #cbd5e1); background: var(--rich-tabs-list-bg, #f8fafc); }
              button {
                flex: 0 0 auto; border: 0; border-bottom: 3px solid transparent; padding: 11px 16px;
                background: transparent; color: var(--rich-tabs-color, #4a5a68); cursor: pointer; font: inherit; font-weight: 600;
              }
              button[aria-selected="true"] { border-bottom-color: var(--rich-tabs-accent, #3472a9); color: var(--rich-tabs-accent, #3472a9); background: white; }
              button:focus-visible { outline: 2px solid var(--rich-tabs-accent, #3472a9); outline-offset: -2px; }
            </style>
            <div class="tabs"><div class="list" role="tablist"></div><slot></slot></div>`
          root.querySelector('slot')?.addEventListener('slotchange', () => this.renderTabs())
        }
        this.observer?.disconnect()
        this.observer = new MutationObserver(() => this.renderTabs())
        this.observer.observe(this, { attributes: true, childList: true, subtree: true, attributeFilter: ['active', 'title'] })
        this.renderTabs()
      }

      disconnectedCallback() {
        this.observer?.disconnect()
      }

      private renderTabs() {
        const tabs = Array.from(this.children).filter((node): node is HTMLElement => node.localName === TAB_TAG)
        const list = this.shadowRoot?.querySelector<HTMLElement>('.list')
        if (!list || tabs.length === 0) return
        if (!tabs.some(tab => tab.hasAttribute('active'))) tabs[0].setAttribute('active', '')
        list.replaceChildren(...tabs.map((tab, index) => {
          const button = document.createElement('button')
          const isActive = tab.hasAttribute('active')
          button.type = 'button'
          button.setAttribute('role', 'tab')
          button.setAttribute('aria-selected', String(isActive))
          button.textContent = tab.getAttribute('title') || `Tab ${index + 1}`
          button.addEventListener('click', () => {
            tabs.forEach(item => item.toggleAttribute('active', item === tab))
            this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
          })
          button.addEventListener('dblclick', event => {
            if (!this.closest('[contenteditable="true"]')) return
            event.preventDefault()
            const title = window.prompt('Tab title:', tab.getAttribute('title') ?? '')?.trim()
            if (!title) return
            tab.setAttribute('title', title.slice(0, 160))
            this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
          })
          return button
        }))
      }
    }

    window.customElements.define(TABS_TAG, ContentTabsElement)
  }
}
